import React from 'react';
import { clsx } from 'clsx';
import type { LoadingProps } from '@/types/ui';

/**
 * Webcoda-styled Loading component
 * Shows spinner with optional text and overlay
 */
export const Loading: React.FC<LoadingProps> = ({
  size = 'md',
  text,
  overlay = false,
  fullScreen = false,
  className,
  'data-testid': testId,
  ...props
}) => {
  // Size classes for spinner
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  // Size classes for text
  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Spinner component
  const spinner = (
    <div
      className={clsx(
        'animate-spin rounded-full border-2 border-gray-200 border-t-primary-500',
        sizeClasses[size]
      )}
      role="status"
      aria-label="Loading"
    />
  );

  // Content wrapper
  const content = (
    <div 
      className={clsx(
        'flex flex-col items-center justify-center space-y-2',
        !overlay && !fullScreen ? className : ''
      )}
      data-testid={testId}
      {...props}
    >
      {spinner}
      {text && (
        <p className={clsx('text-gray-600 font-medium', textSizeClasses[size])}>
          {text}
        </p>
      )}
    </div>
  );

  // Full screen overlay
  if (fullScreen) {
    return (
      <div className={clsx(
        'fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm',
        className
      )}>
        {content}
      </div>
    );
  }

  // Standard overlay
  if (overlay) {
    return (
      <div className={clsx(
        'absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm rounded-lg',
        className
      )}>
        {content}
      </div>
    );
  }

  // Inline loading
  return content;
};

/**
 * Skeleton Loading component for content placeholders
 */
export const Skeleton: React.FC<{
  className?: string;
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'circular' | 'rectangular';
  animation?: 'pulse' | 'wave' | 'none';
}> = ({
  className,
  width,
  height,
  variant = 'rectangular',
  animation = 'pulse',
}) => {
  const variantClasses = {
    text: 'h-4 w-full',
    circular: 'rounded-full',
    rectangular: 'rounded',
  };

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-pulse', // Could be enhanced with custom wave animation
    none: '',
  };

  const style = {
    ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
    ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
  };

  return (
    <div
      className={clsx(
        'bg-gray-200',
        variantClasses[variant],
        animationClasses[animation],
        className
      )}
      style={style}
      aria-hidden="true"
    />
  );
};

/**
 * Skeleton Card - Common loading placeholder
 */
export const SkeletonCard: React.FC<{
  lines?: number;
  showAvatar?: boolean;
  className?: string;
}> = ({ lines = 3, showAvatar = false, className }) => (
  <div className={clsx('space-y-3', className)}>
    {showAvatar && (
      <div className="flex items-center space-x-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1 space-y-2">
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={14} />
        </div>
      </div>
    )}
    <div className="space-y-2">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          width={index === lines - 1 ? '75%' : '100%'}
          height={16}
        />
      ))}
    </div>
  </div>
);

/**
 * Table Skeleton - Loading placeholder for tables
 */
export const SkeletonTable: React.FC<{
  rows?: number;
  columns?: number;
  className?: string;
}> = ({ rows = 5, columns = 4, className }) => (
  <div className={clsx('space-y-3', className)}>
    {/* Header */}
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {Array.from({ length: columns }, (_, index) => (
        <Skeleton key={`header-${index}`} height={20} width="80%" />
      ))}
    </div>
    
    {/* Rows */}
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div 
        key={`row-${rowIndex}`}
        className="grid gap-4"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {Array.from({ length: columns }, (_, colIndex) => (
          <Skeleton key={`cell-${rowIndex}-${colIndex}`} height={16} />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Spinner - Simple spinner component
 */
export const Spinner: React.FC<{
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  className?: string;
}> = ({ size = 'md', color = 'text-primary-500', className }) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
  };

  return (
    <div
      className={clsx(
        'animate-spin rounded-full border-2 border-gray-200',
        'border-t-current',
        sizeClasses[size],
        color,
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

// Attach variants to main Loading component
Object.assign(Loading, { Skeleton });
Object.assign(Loading, { SkeletonCard });
Object.assign(Loading, { SkeletonTable });
Object.assign(Loading, { Spinner });